const POPUP_BLOCKED_MESSAGE =
  'Interactive sign-in could not open a Microsoft sign-in window. Allow pop-ups for this site and try again.';

const INTERACTIVE_SIGN_IN_INCOMPLETE_MESSAGE =
  'Interactive sign-in did not complete. Finish or close any Microsoft sign-in windows, then try again.';

const INTERACTION_ALREADY_IN_PROGRESS_MESSAGE =
  'Another Microsoft sign-in is already in progress. Close any existing sign-in windows and try again.';

const SIGN_IN_CANCELLED_MESSAGE = 'Sign in was cancelled. Please try again.';

const UNPROVISIONED_ACCOUNT_MESSAGE =
  'Your Microsoft account is authenticated, but it is not provisioned for this admin portal. Contact a platform admin.';

const PROFILE_LOAD_FAILED_MESSAGE =
  'We could not load your admin profile. Please try again or contact support.';

const USER_FACING_MESSAGES = new Set([
  POPUP_BLOCKED_MESSAGE,
  INTERACTIVE_SIGN_IN_INCOMPLETE_MESSAGE,
  INTERACTION_ALREADY_IN_PROGRESS_MESSAGE,
  SIGN_IN_CANCELLED_MESSAGE,
  UNPROVISIONED_ACCOUNT_MESSAGE,
  PROFILE_LOAD_FAILED_MESSAGE,
]);

const normalizeMessage = (value: unknown): string =>
  typeof value === 'string' ? value.toLowerCase() : '';

const getErrorCode = (error: unknown): string => {
  if (typeof error !== 'object' || error === null || !('errorCode' in error)) {
    return '';
  }

  return normalizeMessage((error as { errorCode?: unknown }).errorCode);
};

export const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Unknown error');

export const buildAdminProfileError = (status: number, backendMessage?: string): Error => {
  if ([401, 403, 404].includes(status)) {
    return new Error(UNPROVISIONED_ACCOUNT_MESSAGE);
  }

  if (backendMessage?.trim()) {
    return new Error(backendMessage.trim());
  }

  return new Error(PROFILE_LOAD_FAILED_MESSAGE);
};

export const normalizeAuthError = (error: unknown): Error => {
  const candidate = toError(error);
  const code = getErrorCode(error);

  if (USER_FACING_MESSAGES.has(candidate.message)) {
    return candidate;
  }

  const message = normalizeMessage(candidate.message);

  if (
    code.includes('popup_window_error') ||
    code.includes('empty_window_error') ||
    message.includes('popup_window_error') ||
    message.includes('empty_window_error')
  ) {
    return new Error(POPUP_BLOCKED_MESSAGE);
  }

  if (
    code.includes('monitor_popup_timeout') ||
    code.includes('monitor_window_timeout') ||
    code.includes('block_nested_popups') ||
    code.includes('state_interaction_type_mismatch') ||
    code.includes('no_token_request_cache_error') ||
    code.includes('unable_to_parse_token_request_cache_error') ||
    code.includes('hash_empty_error') ||
    code.includes('hash_does_not_contain_known_properties') ||
    code.includes('unable_to_parse_state') ||
    code.includes('no_state_in_hash') ||
    message.includes('monitor_popup_timeout') ||
    message.includes('monitor_window_timeout') ||
    message.includes('block_nested_popups') ||
    message.includes('state_interaction_type_mismatch') ||
    message.includes('no_token_request_cache_error') ||
    message.includes('unable_to_parse_token_request_cache_error') ||
    message.includes('hash_empty_error') ||
    message.includes('hash_does_not_contain_known_properties') ||
    message.includes('unable_to_parse_state') ||
    message.includes('no_state_in_hash')
  ) {
    return new Error(INTERACTIVE_SIGN_IN_INCOMPLETE_MESSAGE);
  }

  if (code.includes('interaction_in_progress') || message.includes('interaction_in_progress')) {
    return new Error(INTERACTION_ALREADY_IN_PROGRESS_MESSAGE);
  }

  if (
    message.includes('user_cancelled') ||
    message.includes('user cancelled') ||
    message.includes('user canceled')
  ) {
    return new Error(SIGN_IN_CANCELLED_MESSAGE);
  }

  return candidate;
};

export const sanitizeAuthErrorMessage = (error: Error): string => {
  const normalized = normalizeAuthError(error);

  if (USER_FACING_MESSAGES.has(normalized.message)) {
    return normalized.message;
  }

  const message = normalizeMessage(normalized.message);

  if (message.includes('network') || message.includes('fetch')) {
    return 'Unable to connect. Please check your internet connection and try again.';
  }

  if (message.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }

  if (message.includes('invalid_grant') || message.includes('invalid_token')) {
    return 'Your session has expired. Please sign in again.';
  }

  if (message.includes('consent') || message.includes('permission')) {
    return 'Additional permissions are required. Please contact your administrator.';
  }

  return 'Authentication failed. Please try again or contact support if the issue persists.';
};

export const isInteractionRequiredError = (error: unknown): boolean => {
  const candidate = toError(error);
  const code = getErrorCode(error);
  const message = normalizeMessage(candidate.message);

  return (
    code.includes('interaction_required') ||
    code.includes('consent_required') ||
    code.includes('login_required') ||
    message.includes('interaction_required') ||
    message.includes('consent_required') ||
    message.includes('login_required')
  );
};

export const isRecoverableInteractionStateError = (error: unknown): boolean => {
  const candidate = toError(error);
  const code = getErrorCode(error);
  const message = normalizeMessage(candidate.message);

  return (
    code.includes('interaction_in_progress') ||
    code.includes('block_nested_popups') ||
    code.includes('state_interaction_type_mismatch') ||
    code.includes('no_token_request_cache_error') ||
    code.includes('unable_to_parse_token_request_cache_error') ||
    code.includes('hash_empty_error') ||
    code.includes('hash_does_not_contain_known_properties') ||
    code.includes('unable_to_parse_state') ||
    code.includes('no_state_in_hash') ||
    message.includes('interaction_in_progress') ||
    message.includes('block_nested_popups') ||
    message.includes('state_interaction_type_mismatch') ||
    message.includes('no_token_request_cache_error') ||
    message.includes('unable_to_parse_token_request_cache_error') ||
    message.includes('hash_empty_error') ||
    message.includes('hash_does_not_contain_known_properties') ||
    message.includes('unable_to_parse_state') ||
    message.includes('no_state_in_hash')
  );
};

export const getInteractiveAuthScopes = (loginScopes: string[], apiScopes: string[]): string[] =>
  Array.from(new Set([...loginScopes, ...apiScopes].filter(Boolean)));

export {
  INTERACTION_ALREADY_IN_PROGRESS_MESSAGE,
  INTERACTIVE_SIGN_IN_INCOMPLETE_MESSAGE,
  POPUP_BLOCKED_MESSAGE,
  PROFILE_LOAD_FAILED_MESSAGE,
  SIGN_IN_CANCELLED_MESSAGE,
  UNPROVISIONED_ACCOUNT_MESSAGE,
};
